import type { CSSProperties, ReactNode } from 'react';

// Placeholder shapes for panels whose layout is known before the data arrives. Give each one the
// size of the content it stands in for, so nothing jumps when it fills in. Panels whose layout
// depends on the answer keep a short status text instead.

/** One grey block. Hidden from screen readers; the pulse stops under prefers-reduced-motion. */
export function Skeleton({ className = '', style }: { className?: string; style?: CSSProperties }) {
  return (
    <span
      aria-hidden="true"
      style={style}
      className={`block rounded-md bg-border motion-safe:animate-pulse ${className}`.trim()}
    />
  );
}

/** The wrapper while loading: aria-busy on the container plus one status line for screen readers. */
export function SkeletonRegion({
  label,
  className = '',
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div aria-busy="true" className={className}>
      <span role="status" className="sr-only">
        {label}
      </span>
      {children}
    </div>
  );
}

/** A stack of equally tall rows: the typical count, not a full page. */
export function SkeletonRows({
  label,
  rows = 4,
  rowClassName = 'h-12',
  className = 'flex flex-col gap-2',
}: {
  label: string;
  rows?: number;
  rowClassName?: string;
  className?: string;
}) {
  return (
    <SkeletonRegion label={label} className={className}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className={`rounded-xl ${rowClassName}`} />
      ))}
    </SkeletonRegion>
  );
}
